export type CalendarMonthDay = {
  key: string;
  year: number;
  month: number;
  day: number;
  inCurrentMonth: boolean;
};

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

export function viewerTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export function civilDayKey(year: number, month: number, day: number): string {
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

export function addCivilDays(dayKey: string, delta: number): string {
  const [year, month, day] = dayKey.split('-').map(Number);
  const next = new Date(Date.UTC(year, month - 1, day + delta));
  return civilDayKey(next.getUTCFullYear(), next.getUTCMonth() + 1, next.getUTCDate());
}

export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

type ZoneParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

function partsInZone(ms: number, timeZone: string): ZoneParts {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(ms));
  const get = (type: Intl.DateTimeFormatPartTypes) => (
    parts.find((part) => part.type === type)?.value ?? ''
  );
  return {
    year: Number(get('year')),
    month: Number(get('month')),
    day: Number(get('day')),
    hour: Number(get('hour')),
    minute: Number(get('minute')),
    second: Number(get('second')),
  };
}

export function zonedLocalToUtcIso(
  dayKey: string,
  hour: number,
  minute: number,
  second: number,
  timeZone: string,
  millisecond = 0,
): string {
  const [year, month, day] = dayKey.split('-').map(Number);
  const desiredUtcMinutes = Date.UTC(year, month - 1, day, hour, minute, second) / 60_000;
  let utc = Date.UTC(year, month - 1, day, hour, minute, second, millisecond);

  for (let attempt = 0; attempt < 3; attempt += 1) {
    const actual = partsInZone(utc, timeZone);
    const actualUtcMinutes = Date.UTC(
      actual.year,
      actual.month - 1,
      actual.day,
      actual.hour,
      actual.minute,
      actual.second,
    ) / 60_000;
    const deltaMinutes = desiredUtcMinutes - actualUtcMinutes;
    if (deltaMinutes === 0) break;
    utc += deltaMinutes * 60_000;
  }

  return new Date(utc - (utc % 1000) + millisecond).toISOString();
}

export function calendarQueryRange(
  year: number,
  month: number,
  timeZone: string,
): { from: string; to: string } {
  const first = civilDayKey(year, month, 1);
  const last = civilDayKey(year, month, daysInMonth(year, month));
  return {
    from: zonedLocalToUtcIso(addCivilDays(first, -7), 0, 0, 0, timeZone),
    to: zonedLocalToUtcIso(addCivilDays(last, 7), 23, 59, 59, timeZone, 999),
  };
}

function weekdayInZone(dayKey: string, timeZone: string): number {
  const iso = zonedLocalToUtcIso(dayKey, 12, 0, 0, timeZone);
  const label = new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short',
  }).format(new Date(iso));
  return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(label);
}

function parseDay(key: string, inCurrentMonth: boolean): CalendarMonthDay {
  const [year, month, day] = key.split('-').map(Number);
  return { key, year, month, day, inCurrentMonth };
}

/** Week-aligned month cells. Always index publishes by these keys — never by
 *  `bucketPublishesByLocalDay` Map iteration order. */
export function buildCalendarMonthDays(
  year: number,
  month: number,
  timeZone: string,
  weekStartsOn: 0 | 1 = 0,
): CalendarMonthDay[] {
  const firstKey = civilDayKey(year, month, 1);
  const leading = (weekdayInZone(firstKey, timeZone) - weekStartsOn + 7) % 7;
  const days: CalendarMonthDay[] = [];

  for (let offset = leading; offset > 0; offset -= 1) {
    days.push(parseDay(addCivilDays(firstKey, -offset), false));
  }

  const dim = daysInMonth(year, month);
  for (let day = 1; day <= dim; day += 1) {
    days.push(parseDay(civilDayKey(year, month, day), true));
  }

  while (days.length % 7 !== 0) {
    const last = days[days.length - 1];
    days.push(parseDay(addCivilDays(last.key, 1), false));
  }

  return days;
}

export function defaultScheduledAtIso(dayKey: string, timeZone: string): string {
  return zonedLocalToUtcIso(dayKey, 10, 0, 0, timeZone);
}

export function isoToDatetimeLocalValue(iso: string, timeZone: string): string {
  const parts = partsInZone(Date.parse(iso), timeZone);
  return `${civilDayKey(parts.year, parts.month, parts.day)}T${pad2(parts.hour)}:${pad2(parts.minute)}`;
}

export function splitDatetimeLocal(
  value: string,
): { dayKey: string; time: string } | null {
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;
  return { dayKey: match[1], time: `${match[2]}:${match[3]}` };
}

export function joinDatetimeLocal(dayKey: string, time: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dayKey) || !/^\d{2}:\d{2}$/.test(time)) return null;
  return `${dayKey}T${time}`;
}

export function datetimeLocalValueToIso(value: string, timeZone: string): string | null {
  const parts = splitDatetimeLocal(value);
  if (!parts) return null;
  const [hour, minute] = parts.time.split(':').map(Number);
  return zonedLocalToUtcIso(parts.dayKey, hour, minute, 0, timeZone);
}

export function shiftYearMonth(
  year: number,
  month: number,
  delta: number,
): { year: number; month: number } {
  const next = new Date(Date.UTC(year, month - 1 + delta, 1));
  return { year: next.getUTCFullYear(), month: next.getUTCMonth() + 1 };
}

export function formatLocalTime(iso: string, timeZone: string, locale?: string): string {
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(iso));
}

export function resolveCalendarItemTitle(row: {
  title?: string | null;
  caption?: string | null;
  clip_name?: string | null;
}): string {
  const title = row.title?.trim();
  if (title) return title;
  const caption = row.caption?.trim();
  if (caption) return caption;
  return row.clip_name?.trim() || '';
}

export function localYearMonth(timeZone: string, now: Date): { year: number; month: number } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(now);
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? '';
  return { year: Number(get('year')), month: Number(get('month')) };
}
